import { v4 as uuid } from 'uuid';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { submitStoryAsTemplateBody, templateSubmitResult } from '@/infrastructure/api/v1/mappers/templateSubmissions';
import { isTemplateProjectCategory } from './templateProjects';
import { toTemplateSubmission } from '@/modules/databaseService/templateSubmissions';
import type { components } from '@/generated/contracts/api-v1';
import type { Services } from '@/infrastructure/services';
import type { TemplateSubmission } from '@/types/templateSubmissionTypes';

export interface TemplateLineage {
  sceneTypeId: string;
  // Null while a lineage exists on paper (a scene_types row) but its template
  // instance has been deleted.
  templateSceneInstanceId: string | null;
  templateSceneOriginId: string | null;
  submission: TemplateSubmission | null;
  // True for templates converted in place before templates became a publish
  // destination: the template instance still sits on the creator's own origin.
  legacyInPlace: boolean;
}

// Resolves the template a story has already produced. scene_types is the durable
// identity here: it survives resubmission, owns the creator's earnings
// (CreatorTemplateUsageService reads scene_types.user_id) and carries the link
// back to the source story in scene_type_source_name.
export async function resolveTemplateLineage(
  services: Services,
  sourceSceneOriginId: string,
  userId: string,
): Promise<TemplateLineage | null> {
  const { data: sceneTypes, error: typesError } = await toResult(v1.GET('/api/v1/scene-types', {
    params: { query: { sourceStoryUniqueId: sourceSceneOriginId, userUniqueId: userId, limit: 1 } },
  }));
  if (typesError) throw typesError;
  const sceneType = sceneTypes?.items[0];

  if (sceneType?.uniqueId) {
    const { data: instances, error: instancesError } = await toResult(v1.GET('/api/v1/scenes', {
      params: { query: { sceneTypeUniqueId: sceneType.uniqueId, status: ['template'], userUniqueId: userId, sort: '-updatedAt', limit: 1 } },
    }));
    if (instancesError) throw instancesError;
    const copy = instances?.items[0];
    const submission = copy?.storyUniqueId
      ? await services.templateSubmissions.fetchBySceneOriginId(copy.storyUniqueId)
      : null;
    return {
      sceneTypeId: sceneType.uniqueId,
      templateSceneInstanceId: copy?.uniqueId ?? null,
      templateSceneOriginId: copy?.storyUniqueId ?? null,
      submission,
      legacyInPlace: false,
    };
  }

  const { data: inPlace, error: inPlaceError } = await toResult(v1.GET('/api/v1/scenes', {
    params: { query: { storyUniqueId: [sourceSceneOriginId], status: ['template'], sort: '-updatedAt', limit: 1 } },
  }));
  if (inPlaceError) throw inPlaceError;
  const legacy = inPlace?.items[0];
  if (!legacy) return null;

  // Called with a template copy's own origin, this fallback would otherwise match
  // the copy against itself and report it as its own legacy in-place template.
  if (await livesInTemplatesProject(legacy.projectUniqueId)) return null;

  // A story started from someone else's template carries their scene_type_id.
  // Adopting it would attach this submission to their manifest and earnings.
  const ownSceneTypeId = legacy.sceneTypeUniqueId
    ? await sceneTypeOwnedBy(legacy.sceneTypeUniqueId, userId)
    : null;

  return {
    sceneTypeId: ownSceneTypeId ?? '',
    templateSceneInstanceId: legacy.uniqueId,
    templateSceneOriginId: sourceSceneOriginId,
    submission: await services.templateSubmissions.fetchBySceneOriginId(sourceSceneOriginId),
    legacyInPlace: true,
  };
}

/** The template a story has produced, as getStoryWorkspace resolved it for the caller the way resolveTemplateLineage does. */
export function templateLineageFrom(status: components['schemas']['StoryTemplateStatusDto'] | null): TemplateLineage | null {
  if (!status) return null;
  return {
    sceneTypeId: status.sceneTypeUniqueId ?? '',
    templateSceneInstanceId: status.templateScene?.uniqueId ?? null,
    templateSceneOriginId: status.templateStoryUniqueId,
    submission: status.submission ? toTemplateSubmission(status.submission) : null,
    legacyInPlace: status.legacyInPlace,
  };
}

async function sceneTypeOwnedBy(sceneTypeId: string, userId: string): Promise<string | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/scene-types/{sceneTypeUniqueId}', {
    params: { path: { sceneTypeUniqueId: sceneTypeId } },
  }));
  if (error && error.status !== 404) throw error;
  return data?.userUniqueId === userId ? sceneTypeId : null;
}

// True for a scene sitting in either template container: a copy the user pulled
// from the gallery, or a published snapshot one of their submissions produced.
async function livesInTemplatesProject(projectId: string | null): Promise<boolean> {
  if (!projectId) return false;
  const { data: project, error } = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}', {
    params: { path: { projectUniqueId: projectId } },
  }));
  if (error && error.status !== 404 && error.status !== 403) throw error;
  return isTemplateProjectCategory(project?.category);
}

export interface SubmitTemplateParams {
  // The story version the creator chose. It is copied, never converted.
  // The server takes the story, owner, email and plan from the scene and the session.
  sceneInstanceId: string;
  storyName: string;
  description: string;
  learnings: string;
  category: string;
  difficulty: string;
  tags: string[];
  features: string[];
}

export interface SubmitTemplateResult {
  templateSceneInstanceId: string;
  templateSceneOriginId: string;
  templateSubmissionId: string;
  isResubmission: boolean;
}

// The copy's id per story version until a submit succeeds, so a retry after a lost
// response gets the first submit's result back instead of copying again.
const pendingTemplateSceneIds = new Map<string, string>();

// One server operation copies the version into the published-templates project,
// gives the copy its listing and gallery media, records or resubmits the
// submission, retires the previous copy and sends the Slack review card.
export async function submitStoryAsTemplate(
  services: Services,
  params: SubmitTemplateParams,
): Promise<SubmitTemplateResult> {
  const { sceneInstanceId } = params;
  const templateSceneId = pendingTemplateSceneIds.get(sceneInstanceId) ?? uuid();
  pendingTemplateSceneIds.set(sceneInstanceId, templateSceneId);

  const { data, error } = await toResult(v1.POST('/api/v1/template-submissions/from-story', {
    body: submitStoryAsTemplateBody({
      templateSceneId,
      sceneInstanceId,
      storyName: params.storyName,
      description: params.description,
      learnings: params.learnings,
      category: params.category,
      difficulty: params.difficulty,
      tags: params.tags,
      features: params.features,
    }),
  }));
  if (error) throw error;
  pendingTemplateSceneIds.delete(sceneInstanceId);

  const result = templateSubmitResult(data);
  try {
    services.analytics.trackLureoTemplateSubmitted({
      template_scene_instance_id: result.templateSceneInstanceId,
      template_submission_id: result.templateSubmissionId,
      is_resubmission: result.isResubmission,
      should_be_paid: result.submission.should_be_paid,
    });
  } catch (trackError) {
    console.error('Template submitted, but tracking failed', trackError);
  }

  return {
    templateSceneInstanceId: result.templateSceneInstanceId,
    templateSceneOriginId: result.templateSceneOriginId,
    templateSubmissionId: result.templateSubmissionId,
    isResubmission: result.isResubmission,
  };
}
